/**
 * @name ProdutoContext
 * @author Keven Adan Bezerra | kevenadanb
 * @description Context para gerenciar produtos e categorias do totem
 * @package src/contexts/ProdutoContext/ProdutoContext.tsx
 */

import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAlert } from "../../hooks/useAlert";
import { useLoading } from "../../hooks/useLoading";
import { restricoesAlimentaresService } from "../../services/supabase/entities/RestricoesAlimentaresService";
import { useTotemServices } from "../../services/supabase/entities/useTotemServices";
import { IProdutosGrupos } from "../../types/tables/IProdutosGrupos";

// Types
export interface ICategoriaProduto {
  id: number;
  nome: string;
  icone: string;
  cor: string;
  ordem: number;
  ativo: boolean;
}

export interface IRestricaoAlimentar {
  id: number;
  nome: string;
  icone: string;
  cor: string;
}

export interface IProdutoTotem {
  id: number;
  nome: string;
  descricao: string;
  preco: number;
  categoria_id: number;
  categoria_nome: string;

  // NOVOS CAMPOS PARA SUBGRUPOS
  subcategoria_id?: number | null;
  subcategoria_nome?: string | null;

  imagem: string | null;
  disponivel: boolean;
  restricoes: IRestricaoAlimentar[];
  ingredientes: string[];
  combo: boolean;
  combo_itens?: IProdutoTotem[];
  ordem: number;
}

export interface IProdutoContext {
  // Estado dos produtos
  produtos: IProdutoTotem[];
  categorias: ICategoriaProduto[];
  categoriaAtiva: number | null;

  // Filtros e busca
  produtosFiltrados: IProdutoTotem[];
  termoBusca: string;

  // Ações
  carregarProdutos: () => Promise<void>;
  carregarProdutosParaAluno: (alunoId: number) => Promise<void>;
  carregarCategorias: () => Promise<void>;
  setCategoriaAtiva: (categoriaId: number | null) => void;
  setBusca: (termo: string) => void;
  verificarRestricoes: (
    produto: IProdutoTotem,
    restricoesAluno: string[],
  ) => boolean;

  // Estados
  isCarregandoProdutos: boolean;
}

// Context
export const ProdutoContext = createContext<IProdutoContext>(
  {} as IProdutoContext,
);

// Provider Props
interface ProdutoProviderProps {
  children: React.ReactNode;
}

/**
 * Provider do contexto de produtos
 */
export const ProdutoProvider: React.FC<ProdutoProviderProps> = ({
  children,
}) => {
  const { showError } = useAlert();
  const { setLoadingState } = useLoading();
  const {
    produtos: produtosService,
    grupos: gruposService,
    context,
  } = useTotemServices();

  const [produtos, setProdutos] = useState<IProdutoTotem[]>([]);
  const [categorias, setCategorias] = useState<ICategoriaProduto[]>([]);
  const [categoriaAtiva, setCategoriaAtiva] = useState<number | null>(null);
  const [termoBusca, setTermoBusca] = useState("");
  const [isCarregandoProdutos, setIsCarregandoProdutos] = useState(false);

  // Mapear cor padrão por categoria
  const getCategoriaIconeCor = (
    nomeCategoria: string,
  ): { icone: string; cor: string } => {
    // Debug: Log do nome da categoria para identificar o problema
    if (nomeCategoria.toLowerCase().includes("sorvete")) {
      console.log("🍦 DEBUG Categoria Sorvetes:", { nomeCategoria });
    }

    const mapeamento: Record<string, { icone: string; cor: string }> = {
      Lanches: { icone: "🥪", cor: "#FF6B6B" },
      Bebidas: { icone: "🥤", cor: "#4ECDC4" },
      Doces: { icone: "🍭", cor: "#FFE66D" },
      "Doces e Diversos": { icone: "🍭", cor: "#FFE66D" },
      "DOCES E DIVERSOS": { icone: "🍭", cor: "#FFE66D" },
      Combos: { icone: "🍽️", cor: "#A8E6CF" },
      Salgados: { icone: "🥐", cor: "#FF8B94" },
      Frutas: { icone: "🍎", cor: "#95E1D3" },
      Refeições: { icone: "🍛", cor: "#F38BA8" },
      Sobremesas: { icone: "🧁", cor: "#DDA0DD" },
      Sorvetes: { icone: "🍦", cor: "#87CEEB" },
      SORVETES: { icone: "🍦", cor: "#87CEEB" },
      Outros: { icone: "🍴", cor: "#B8B8B8" },
    };

    // Verificação direta primeiro
    if (mapeamento[nomeCategoria]) {
      return mapeamento[nomeCategoria];
    }

    // Verificação case-insensitive para sorvetes
    const nomeNormalizado = nomeCategoria.toLowerCase().trim();
    if (nomeNormalizado.includes("sorvete")) {
      return { icone: "🍦", cor: "#87CEEB" };
    }

    return { icone: "🍴", cor: "#B8B8B8" };
  };

  // Carregar categorias (grupos de produtos) reais
  const carregarCategorias = useCallback(async () => {
    console.log("🏷️ [carregarCategorias] Iniciando função...");

    // Aguardar organização estar disponível
    if (!context.organizationId) {
      console.log(
        "⚠️ [carregarCategorias] Aguardando organização para carregar categorias...",
      );
      console.log("📋 [carregarCategorias] context:", context);
      return;
    }

    try {
      setLoadingState("categorias-loading", true);
      console.log(
        "🏷️ [carregarCategorias] Carregando categorias para organização:",
        context.organizationId,
      );

      // Verificar se o service está realmente disponível
      console.log(
        "🔍 [carregarCategorias] Verificando disponibilidade do service...",
      );
      console.log("🔍 [carregarCategorias] gruposService:", gruposService);
      console.log(
        "🔍 [carregarCategorias] gruposService.getAllGrupos:",
        gruposService?.getAllGrupos,
      );
      console.log(
        "🔍 [carregarCategorias] typeof getAllGrupos:",
        typeof gruposService?.getAllGrupos,
      );

      if (!gruposService || typeof gruposService.getAllGrupos !== "function") {
        console.log(
          "⚠️ [carregarCategorias] Service de grupos não está disponível ainda...",
        );
        return;
      }

      console.log("🚀 [carregarCategorias] Chamando getAllGrupos()...");
      const response = await gruposService.getAllGrupos();
      console.log(
        "📋 [carregarCategorias] Resposta COMPLETA do getAllGrupos:",
        response,
      );
      console.log("📋 [carregarCategorias] Tipo da resposta:", typeof response);
      console.log(
        "📋 [carregarCategorias] Keys da resposta:",
        Object.keys(response || {}),
      );

      if (response.error) {
        console.error("❌ Erro na resposta do service:", response.error);
        throw new Error(response.error.message);
      }

      if (response.data) {
        console.log("📋 Dados recebidos do service:", {
          type: typeof response.data,
          isArray: Array.isArray(response.data),
          length: Array.isArray(response.data)
            ? response.data.length
            : "not array",
          firstItem:
            Array.isArray(response.data) && response.data.length > 0
              ? response.data[0]
              : "no items",
        });

        // Garantir que temos um array de IProdutosGrupos
        let grupos: IProdutosGrupos[] = [];

        if (Array.isArray(response.data)) {
          // Se cada item é uma array (problema identificado), fazer flat
          if (response.data.length > 0 && Array.isArray(response.data[0])) {
            console.log("🔧 Detectado array aninhado, fazendo flat...");
            grupos = (response.data as unknown as IProdutosGrupos[][]).flat();
          } else {
            grupos = response.data as unknown as IProdutosGrupos[];
          }
        } else {
          console.log("⚠️ Resposta não é um array, tentando converter...");
          grupos = [response.data as unknown as IProdutosGrupos];
        }

        console.log(
          `📋 ${grupos.length} grupos para processar após normalização`,
        );

        // Validar e filtrar grupos válidos
        const gruposValidos = grupos.filter(
          (grupo: any) =>
            grupo &&
            typeof grupo === "object" &&
            grupo.id &&
            grupo.descricao &&
            !grupo.deleted_at,
        );

        console.log(`📋 ${gruposValidos.length} grupos válidos encontrados`);

        if (gruposValidos.length === 0) {
          console.log("⚠️ Nenhum grupo válido encontrado");
          setCategorias([]);
          return;
        }

        const categoriasFormatadas: ICategoriaProduto[] = gruposValidos.map(
          (grupo: any, index: number) => {
            const { icone, cor } = getCategoriaIconeCor(grupo.descricao || "");

            const categoria = {
              id: Number(grupo.id),
              nome: String(grupo.descricao || ""),
              icone,
              cor,
              ordem: index + 1,
              ativo: true,
            };

            console.log(`📋 Categoria formatada ${index + 1}:`, categoria);
            return categoria;
          },
        );

        setCategorias(categoriasFormatadas);
        console.log(
          `✅ ${categoriasFormatadas.length} categorias carregadas para organização ${context.organizationId}`,
        );
        console.log(
          "📊 IDs das categorias carregadas:",
          categoriasFormatadas.map((cat) => ({ id: cat.id, nome: cat.nome })),
        );

        // Definir primeira categoria como ativa se ainda não há uma
        if (categoriasFormatadas.length > 0 && categoriaAtiva === null) {
          setCategoriaAtiva(categoriasFormatadas[0].id);
          console.log(
            `🎯 Primeira categoria definida como ativa: ID ${categoriasFormatadas[0].id} - ${categoriasFormatadas[0].nome}`,
          );
        }
      } else {
        console.log("⚠️ Nenhuma categoria encontrada para a organização");
        setCategorias([]);
      }
    } catch (error) {
      console.error("❌ Erro ao carregar categorias:", error);
      showError("Erro ao carregar categorias: " + (error as Error).message);
      setCategorias([]);
    } finally {
      setLoadingState("categorias-loading", false);
    }
  }, [
    context.organizationId,
    gruposService,
    showError,
    setLoadingState,
    categoriaAtiva,
  ]);

  // Carregar produtos reais
  // Carregar produtos reais usando relacionamentos database-driven
  const carregarProdutos = useCallback(async () => {
    // Aguardar organização estar disponível
    if (!context.organizationId) {
      console.log("⚠️ Aguardando organização para carregar produtos...");
      return;
    }

    try {
      setIsCarregandoProdutos(true);
      setLoadingState("produtos-loading", true);
      console.log(
        "📦 Carregando produtos com relacionamentos para organização:",
        context.organizationId,
      );

      // Usar método que já traz as categorias relacionadas
      const produtosResponse = await produtosService.getAllWithCategories();

      if (produtosResponse.error) {
        throw new Error(`Erro produtos: ${produtosResponse.error.message}`);
      }

      const produtosFormatados: IProdutoTotem[] = [];

      // Processar produtos individuais com relacionamentos reais
      if (produtosResponse.data && Array.isArray(produtosResponse.data)) {
        console.log(
          `📋 Processando ${produtosResponse.data.length} produtos com relacionamentos...`,
        );

        // Tratar dados como array de arrays e fazer flat se necessário
        let produtos: any[] = [];
        if (
          Array.isArray(produtosResponse.data) &&
          produtosResponse.data.length > 0 &&
          Array.isArray(produtosResponse.data[0])
        ) {
          produtos = produtosResponse.data.flat();
        } else {
          produtos = produtosResponse.data;
        }

        const produtosComCategorias = produtos.map((produto: any) => {
          // Usar relacionamentos reais em vez de determinarCategoria
          const categoriaId = produto.produtos_grupo?.id || 0;
          const categoriaNome =
            produto.produtos_grupo?.descricao || "Sem Categoria";
          const subcategoriaId = produto.produtos_sub_grupo?.id || null;
          const subcategoriaNome =
            produto.produtos_sub_grupo?.descricao || null;

          return {
            id: produto.id,
            nome: produto.descricao || "",
            descricao: produto.descricao_reduzida || produto.descricao || "",
            preco: produto.valor || 0,

            // Usar relacionamentos reais do banco
            categoria_id: categoriaId,
            categoria_nome: categoriaNome,
            subcategoria_id: subcategoriaId,
            subcategoria_nome: subcategoriaNome,

            imagem: produto.url_storage || produto.foto || null,
            disponivel:
              !produto.controle_estoque || produto.quantidade_estoque > 0,
            restricoes: [], // TODO: Implementar restrições alimentares
            ingredientes: [], // TODO: Implementar ingredientes
            combo: false,
            ordem: 1,
          };
        });

        console.log(
          `📋 ${produtosComCategorias.length} produtos individuais processados com relacionamentos`,
        );

        // Log dos primeiros produtos com suas categorias reais
        produtosComCategorias.slice(0, 5).forEach((produto: IProdutoTotem) => {
          console.log(
            `📦 Produto: ${produto.nome} -> Categoria: ${produto.categoria_id} (${produto.categoria_nome}) | Sub: ${produto.subcategoria_nome || "Nenhuma"}`,
          );
        });

        produtosFormatados.push(...produtosComCategorias);
      }

      setProdutos(produtosFormatados);
      console.log(
        `✅ ${produtosFormatados.length} produtos carregados com relacionamentos para organização ${context.organizationId}`,
      );
    } catch (error) {
      console.error("❌ Erro ao carregar produtos:", error);
      showError("Erro ao carregar produtos");
      setProdutos([]);
    } finally {
      setIsCarregandoProdutos(false);
      setLoadingState("produtos-loading", false);
    }
  }, [
    context.organizationId,
    gruposService,
    produtosService,
    showError,
    setLoadingState,
    categoriaAtiva,
  ]);

  // Carregar produtos considerando restrições do aluno usando relacionamentos reais
  const carregarProdutosParaAluno = useCallback(
    async (alunoId: number) => {
      // Aguardar organização estar disponível
      if (!context.organizationId) {
        console.log(
          "⚠️ Aguardando organização para carregar produtos do aluno...",
        );
        return;
      }

      try {
        setIsCarregandoProdutos(true);
        setLoadingState("produtos-loading", true);
        console.log(
          "🛡️ Carregando produtos para aluno usando relacionamentos:",
          { alunoId, organizationId: context.organizationId },
        );

        // Carregar produtos disponíveis com categorias e restrições do aluno em paralelo
        const [produtosResponse, restricoesAluno] = await Promise.all([
          produtosService.getDisponiveis(),
          restricoesAlimentaresService.getByAluno(
            context.organizationId,
            alunoId,
          ),
        ]);

        if (restricoesAluno.length > 0) {
          console.log(
            `⚠️ Aluno ${alunoId} possui ${restricoesAluno.length} restrição(ões) alimentar(es) - produtos restritos serão filtrados`,
          );
        }

        if (produtosResponse.error) {
          throw new Error(`Erro produtos: ${produtosResponse.error.message}`);
        }

        const produtosFormatados: IProdutoTotem[] = [];

        // Processar produtos considerando restrições
        if (produtosResponse.data && Array.isArray(produtosResponse.data)) {
          console.log(
            `📋 Processando ${produtosResponse.data.length} produtos com relacionamentos...`,
          );
          console.log(
            `🛡️ Aluno possui ${restricoesAluno.length} restrições alimentares`,
          );

          // Tratar dados como array de arrays e fazer flat se necessário
          let produtos: any[] = [];
          if (
            Array.isArray(produtosResponse.data) &&
            produtosResponse.data.length > 0 &&
            Array.isArray(produtosResponse.data[0])
          ) {
            produtos = produtosResponse.data.flat();
          } else {
            produtos = produtosResponse.data;
          }

          for (const produto of produtos) {
            if (!produto || !produto.id) continue; // Verificação de segurança

            // Verificar se o produto está restrito para este aluno
            const temRestricao = restricoesAluno.some(
              (restricao: any) => restricao.fk_id_produto === produto.id,
            );

            if (!temRestricao) {
              // Usar relacionamentos reais do banco
              const categoriaId = produto.produtos_grupo?.id || 0;
              const categoriaNome =
                produto.produtos_grupo?.descricao || "Sem Categoria";
              const subcategoriaId = produto.produtos_sub_grupo?.id || null;
              const subcategoriaNome =
                produto.produtos_sub_grupo?.descricao || null;

              const produtoFormatado: IProdutoTotem = {
                id: produto.id || 0,
                nome: produto.descricao || "",
                descricao:
                  produto.descricao_reduzida || produto.descricao || "",
                preco: produto.valor || 0,

                // Usar relacionamentos reais do banco
                categoria_id: categoriaId,
                categoria_nome: categoriaNome,
                subcategoria_id: subcategoriaId,
                subcategoria_nome: subcategoriaNome,

                imagem: produto.url_storage || produto.foto || null,
                disponivel: true, // Produtos já filtrados como disponíveis
                restricoes: [],
                ingredientes: [],
                combo: false,
                combo_itens: [],
                ordem: 1,
              };

              console.log(
                `📦 Produto para aluno: ${produtoFormatado.nome} -> Cat ID: ${categoriaId} (${categoriaNome})`,
              );
              produtosFormatados.push(produtoFormatado);
            } else {
              console.log(
                `🛡️ Produto "${produto.descricao}" restrito para aluno ${alunoId}`,
              );
            }
          }
        }

        setProdutos(produtosFormatados);
        console.log(
          `✅ ${produtosFormatados.length} produtos permitidos carregados para aluno ${alunoId}`,
        );
      } catch (error) {
        console.error("❌ Erro ao carregar produtos para aluno:", error);
        showError("Erro ao carregar produtos");
        setProdutos([]);
      } finally {
        setIsCarregandoProdutos(false);
        setLoadingState("produtos-loading", false);
      }
    },
    [context.organizationId, showError, setLoadingState],
  );

  // Filtrar produtos por categoria e busca
  const produtosFiltrados = useMemo(() => {
    console.log("🔍 [produtosFiltrados] Iniciando filtragem...");
    console.log("🔍 Total de produtos:", produtos.length);
    console.log("🔍 Categoria ativa:", categoriaAtiva);
    console.log("🔍 Termo de busca:", termoBusca);

    let resultado = produtos;

    // Filtrar por categoria
    if (categoriaAtiva !== null) {
      console.log("🔍 Filtrando por categoria:", categoriaAtiva);
      resultado = resultado.filter((produto) => {
        const match = produto.categoria_id === categoriaAtiva;
        if (!match) {
          console.log("🔍 Produto não passou no filtro:", {
            nome: produto.nome,
            categoria_id: produto.categoria_id,
            categoria_nome: produto.categoria_nome,
            categoria_esperada: categoriaAtiva,
          });
        } else {
          console.log("✅ Produto passou no filtro:", {
            nome: produto.nome,
            categoria_id: produto.categoria_id,
            categoria_nome: produto.categoria_nome,
          });
        }
        return match;
      });
      console.log("🔍 Produtos após filtro por categoria:", resultado.length);
    }

    // Filtrar por termo de busca
    if (termoBusca.trim()) {
      const termo = termoBusca.toLowerCase();
      console.log("🔍 Filtrando por termo:", termo);
      resultado = resultado.filter(
        (produto) =>
          produto.nome.toLowerCase().includes(termo) ||
          produto.descricao.toLowerCase().includes(termo) ||
          produto.ingredientes.some((ing) => ing.toLowerCase().includes(termo)),
      );
      console.log("🔍 Produtos após filtro por busca:", resultado.length);
    }

    // Ordenar por ordem e nome
    const produtosOrdenados = resultado.sort((a, b) => {
      if (a.ordem !== b.ordem) return a.ordem - b.ordem;
      return a.nome.localeCompare(b.nome);
    });

    console.log("🔍 Produtos finais filtrados:", produtosOrdenados.length);
    if (produtosOrdenados.length > 0) {
      console.log("🔍 Primeiro produto filtrado:", {
        nome: produtosOrdenados[0].nome,
        categoria_id: produtosOrdenados[0].categoria_id,
        categoria_nome: produtosOrdenados[0].categoria_nome,
      });
    }

    return produtosOrdenados;
  }, [produtos, categoriaAtiva, termoBusca]);

  // Verificar se produto tem restrições que conflitam com as do aluno
  const verificarRestricoes = useCallback(
    (produto: IProdutoTotem, restricoesAluno: string[]): boolean => {
      if (!restricoesAluno || restricoesAluno.length === 0) return true;

      return !produto.restricoes.some((restricao) =>
        restricoesAluno.includes(restricao.nome.toLowerCase()),
      );
    },
    [],
  );

  // Setter para busca
  const setBusca = useCallback((termo: string) => {
    setTermoBusca(termo);
  }, []);

  // Atualizar nomes das categorias nos produtos quando categorias mudarem
  useEffect(() => {
    if (produtos.length > 0 && categorias.length > 0) {
      setProdutos((prevProdutos) =>
        prevProdutos.map((produto) => {
          const categoria = categorias.find(
            (cat) => cat.id === produto.categoria_id,
          );
          return categoria
            ? { ...produto, categoria_nome: categoria.nome }
            : produto;
        }),
      );
    }
  }, [categorias]);

  // Carregar dados iniciais quando organização estiver disponível
  useEffect(() => {
    console.log("🔄 [useEffect] Monitorando organização e gruposService...");
    console.log(
      "🔄 [useEffect] context.organizationId:",
      context.organizationId,
    );
    console.log("🔄 [useEffect] gruposService:", gruposService);
    console.log("🔄 [useEffect] context completo:", context);

    let isMounted = true;

    const loadInitialData = async () => {
      console.log("🚀 [loadInitialData] Verificando condições...");
      console.log(
        "🚀 [loadInitialData] context.organizationId:",
        context.organizationId,
      );
      console.log("🚀 [loadInitialData] isMounted:", isMounted);
      console.log("🚀 [loadInitialData] gruposService:", !!gruposService);

      // Aguardar organização estar definida
      if (context.organizationId && isMounted && gruposService) {
        console.log(
          "🔄 [loadInitialData] ✅ Organização disponível, carregando dados iniciais...",
        );
        try {
          await carregarCategorias();
        } catch (error) {
          console.error(
            "❌ [loadInitialData] Erro ao carregar categorias iniciais:",
            error,
          );
        }
      } else {
        console.log(
          "🔄 [loadInitialData] ⚠️ Condições não atendidas para carregar dados",
        );
        if (!context.organizationId)
          console.log("🔄 [loadInitialData] - Falta organizationId");
        if (!isMounted)
          console.log("🔄 [loadInitialData] - Componente não montado");
        if (!gruposService)
          console.log("🔄 [loadInitialData] - Falta gruposService");
      }
    };

    loadInitialData();

    return () => {
      console.log("🔄 [useEffect] Cleanup - desmontando...");
      isMounted = false;
    };
  }, [context.organizationId]); // Removendo gruposService para evitar loop - ele muda a cada render

  // Carregar produtos após categorias estarem disponíveis
  useEffect(() => {
    let isMounted = true;

    const loadProducts = async () => {
      // Aguardar organização E categorias estarem disponíveis
      if (
        context.organizationId &&
        categorias.length > 0 &&
        isMounted &&
        produtosService
      ) {
        console.log("🔄 Categorias disponíveis, carregando produtos...");
        try {
          await carregarProdutos();
        } catch (error) {
          console.error("❌ Erro ao carregar produtos:", error);
        }
      }
    };

    loadProducts();

    return () => {
      isMounted = false;
    };
  }, [context.organizationId, categorias.length]); // Removendo produtosService para evitar loop

  // Context value
  const contextValue: IProdutoContext = useMemo(
    () => ({
      produtos,
      categorias,
      categoriaAtiva,
      produtosFiltrados,
      termoBusca,
      carregarProdutos,
      carregarProdutosParaAluno,
      carregarCategorias,
      setCategoriaAtiva,
      setBusca,
      verificarRestricoes,
      isCarregandoProdutos,
    }),
    [
      produtos,
      categorias,
      categoriaAtiva,
      produtosFiltrados,
      termoBusca,
      carregarProdutos,
      carregarProdutosParaAluno,
      carregarCategorias,
      verificarRestricoes,
      isCarregandoProdutos,
    ],
  );

  return (
    <ProdutoContext.Provider value={contextValue}>
      {children}
    </ProdutoContext.Provider>
  );
};
